import React, { FC, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { styled } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import { useEnumState } from '../../hooks/useEnumState';
import Button from '@mui/material/Button';
import Box from '@mui/system/Box';
import { WarningAlert } from '../Alert/WarningAlert';
import { TypographyBody1 } from '../Typography/Typography';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import { CodeInput } from '../CodeInput/CodeInput';
import { HiddenSubmit } from '../HiddenSubmit/HiddenSubmit';
import DialogActions from '@mui/material/DialogActions';
import { apiAuth, EMfaMethod } from '../../resources/api/apiAuth';
import { useApiErrorHandler } from '../../hooks/useApiErrorHandler';
import { NotificationContext } from '../../contexts/NotificationContext';
import { MFAMethodsFetcher } from '../../fetchers/MfaMethodsFetcher';
import { MFADevicesFetcher } from '../../fetchers/MfaDevicesFetcher';
import { TemporaryAuthAppSettings } from './TemporaryAuthAppSettings';
import { DisableDeviceButton } from './DisableDeviceButton';
import { config } from '../../config';

enum ESetupMfaState {
  Closed,
  Open,
  Sent,
}

const TopContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: theme.spacing(1),
}));

const TopSectionTitle = styled('div')({
  flex: 1,
});

const NewLine = styled('div')({
  display: 'block',
  height: '2em',
});

const { useVerify2faSetup, useRegister2faByMethodId } = apiAuth;

export const MfaSettings: FC = () => {
  const { t } = useTranslation();
  const handleError = useApiErrorHandler();
  const { addNotification } = useContext(NotificationContext);

  const { execute: verify2fa } = useVerify2faSetup();
  const { execute: register2faByMethodId } = useRegister2faByMethodId();

  const { data: mfaDevices, onRefresh: onMfaRefresh } = useContext(MFADevicesFetcher.Context);
  const { data: mfaMethodsData } = useContext(MFAMethodsFetcher.Context);

  const emailAuth = mfaMethodsData?.find((method) => method.name === EMfaMethod.Email);

  const emailDevices = useMemo(
    () => (mfaDevices ?? []).filter((device) => device.method_name === EMfaMethod.Email),
    [mfaDevices]
  );

  const [setupMfaState, openSetupMfa, setMfaSent, closeSetupMfa] = useEnumState(
    ESetupMfaState.Closed,
    ESetupMfaState.Open,
    ESetupMfaState.Sent,
    ESetupMfaState.Closed
  );
  const [mfaCode, setMfaCode] = useState('');
  const deviceIdRef = useRef('');

  const onSetupMfa = useCallback(async () => {
    try {
      if (!emailAuth) {
        return;
      }
      const result = await register2faByMethodId({
        method_id: emailAuth.id,
        is_default: true,
        name: '',
      });
      deviceIdRef.current = result.payload.device_id;
      setMfaSent();
    } catch (e) {
      handleError(e);
    }
  }, [register2faByMethodId, emailAuth]);

  const onMfaSubmit = useCallback(async () => {
    try {
      await verify2fa(deviceIdRef.current, mfaCode);
      await onMfaRefresh();
      deviceIdRef.current = '';

      addNotification({
        severity: 'success',
        message: t('security.mfa.successMessage'),
      });
      setMfaCode('');
      closeSetupMfa();
    } catch (e) {
      handleError(e);
    }
  }, [mfaCode]);

  if (!mfaDevices) return null;

  return (
    <div>
      <TopContainer>
        <TopSectionTitle>{t('security.mfa.title')}</TopSectionTitle>
        <div>
          {emailDevices.length ? (
            <DisableDeviceButton device={emailDevices[0]} onRefresh={onMfaRefresh}>
              {t('security.mfa.disable.title')}
            </DisableDeviceButton>
          ) : null}

          {!emailDevices.length ? (
            <Button variant={'contained'} onClick={openSetupMfa}>
              {t('security.mfa.disabled.setupEmail2fa')}
            </Button>
          ) : null}
        </div>
      </TopContainer>
      <Box mt={1} mb={1}>
        {emailDevices.length ? (
          <TypographyBody1 color={'text.disabled'} pr={'30%'}>
            {t('security.mfa.enabledDescription')}
          </TypographyBody1>
        ) : (
          <TypographyBody1 color={'text.disabled'}>{t('security.mfa.description')}</TypographyBody1>
        )}
      </Box>

      {!emailDevices.length ? (
        <WarningAlert>
          <Trans
            i18nKey="security.mfa.discouragement"
            components={{
              Bold: <strong />,
              NewLine: <NewLine />,
            }}
          />
        </WarningAlert>
      ) : null}

      {/*@todo to be removed after release of epic-880*/}
      {config.enableDeveloperTools ? (
        <TemporaryAuthAppSettings setMfaSent={setMfaSent} setDeviceId={(id) => (deviceIdRef.current = id)} />
      ) : null}
      {/*@todo end*/}

      <Dialog open={[ESetupMfaState.Open, ESetupMfaState.Sent].includes(setupMfaState)}>
        <DialogTitle>{t('security.mfa.email.enterCode')}</DialogTitle>

        {setupMfaState === ESetupMfaState.Open ? (
          <DialogContent>
            <Box mb={2}>
              <TypographyBody1>{t('security.mfa.email.beforeSending')}</TypographyBody1>
            </Box>
          </DialogContent>
        ) : null}

        {setupMfaState === ESetupMfaState.Sent ? (
          <DialogContent>
            <TypographyBody1>{t('security.mfa.email.description')}</TypographyBody1>
            <form onSubmit={onMfaSubmit}>
              <CodeInput value={mfaCode} onChange={setMfaCode} pattern={'ddd-ddd'} />
              <HiddenSubmit />
            </form>
          </DialogContent>
        ) : null}

        <DialogActions>
          <Button variant={'outlined'} onClick={closeSetupMfa}>
            {t('common.dialog.close')}
          </Button>

          {setupMfaState === ESetupMfaState.Open ? (
            <Button variant={'contained'} onClick={onSetupMfa}>
              {t('security.mfa.email.send')}
            </Button>
          ) : null}

          {setupMfaState === ESetupMfaState.Sent ? (
            <Button variant={'contained'} onClick={onMfaSubmit} disabled={mfaCode.length < 6}>
              {t('security.mfa.email.submit')}
            </Button>
          ) : null}
        </DialogActions>
      </Dialog>
    </div>
  );
};
